import { useState } from "react";
import Icon from "../../components/ui/Icon";
import Toggle from "../../components/ui/Toggle";
import SlideOverlay from "../../components/overlays/SlideOverlay";
import {
  BATTERY_CAPACITY_AH,
  SOC_WARNING_DEFAULT,
  SOC_WARNING_MIN,
  SOC_WARNING_MAX,
  SOC_WARNING_STEP,
} from "../../constants/app";

// Arc gauge — SVG-based, BRAND_COLOR_MAGENTA fill
function SocArc({ soc = 0 }) {
  const r = 54;
  const cx = 70;
  const cy = 70;
  const startAngle = -220; // degrees, clockwise from 3 o'clock
  const sweep = 260;       // total arc degrees

  const toRad = (deg) => (deg * Math.PI) / 180;

  const arcPath = (pct) => {
    const angle = startAngle + sweep * pct;
    const x1 = cx + r * Math.cos(toRad(startAngle));
    const y1 = cy + r * Math.sin(toRad(startAngle));
    const x2 = cx + r * Math.cos(toRad(angle));
    const y2 = cy + r * Math.sin(toRad(angle));
    const large = sweep * pct > 180 ? 1 : 0;
    return `M ${x1} ${y1} A ${r} ${r} 0 ${large} 1 ${x2} ${y2}`;
  };

  const pct = Math.min(100, Math.max(0, soc)) / 100;

  return (
    <svg width="140" height="140" viewBox="0 0 140 140">
      {/* Track */}
      <path
        d={arcPath(1)}
        fill="none"
        stroke="#211747"
        strokeWidth="10"
        strokeLinecap="round"
      />
      {/* Fill */}
      <path
        d={arcPath(pct)}
        fill="none"
        stroke="#BB16A3"
        strokeWidth="10"
        strokeLinecap="round"
        style={{ filter: "drop-shadow(0 0 6px #BB16A355)" }}
      />
    </svg>
  );
}

// Metric tile
function MetricTile({ label, value, accent = false, icon }) {
  return (
    <div className="rounded-2xl border border-brand-navy bg-brand-navy px-3 py-3 flex flex-col gap-1">
      <div className="flex items-center gap-1.5 text-[10px] font-semibold tracking-widest text-brand-light/50 uppercase">
        {icon && <Icon name={icon} className="text-sm" />}
        {label}
      </div>
      <span className={`text-xl font-black italic ${accent ? "text-brand-magenta" : "text-brand-light"}`}>
        {value}
      </span>
    </div>
  );
}

// Individual cell bar
function CellBar({ index, voltage, allVoltages }) {
  const min = Math.min(...allVoltages);
  const max = Math.max(...allVoltages);
  const range = max - min || 0.01;
  const spread = Math.abs(voltage - min) / range;
  // Highlight cells that deviate from the mean
  const mean = allVoltages.reduce((s, v) => s + v, 0) / allVoltages.length;
  const isOutlier = Math.abs(voltage - mean) > 0.005;

  return (
    <div className="rounded-xl border border-brand-navy bg-brand-navy px-3 py-2.5 flex items-center gap-3">
      <span className="text-[11px] font-semibold text-brand-light/50 w-10 shrink-0">
        Cell {index + 1}
      </span>
      <div className="flex-1 h-1.5 rounded-full bg-brand-purple overflow-hidden">
        <div
          className={`h-full rounded-full transition-all ${isOutlier ? "bg-brand-magenta" : "bg-emerald-500"}`}
          style={{ width: `${30 + spread * 70}%` }}
        />
      </div>
      <span className={`text-xs font-semibold w-12 text-right shrink-0 ${isOutlier ? "text-brand-magenta" : "text-brand-light"}`}>
        {voltage.toFixed(3)} V
      </span>
    </div>
  );
}

export default function BmsConfigOverlay({
  isOpen,
  device,
  bmsName,
  bridgeName,
  showOnDashboard,
  onNameChange,
  onShowOnDashboardChange,
  onSave,
  onClose,
}) {
  const m = device?.metrics ?? {};
  const soc      = m.soc      ?? 85;
  const soh      = m.soh      ?? 98;
  const voltage  = m.voltage  ?? 13.8;
  const current  = m.current  ?? 25.0;
  const power    = m.power    ?? 345;
  const temp     = m.temp     ?? 23;
  const cells    = m.cells    ?? [3.45, 3.46, 3.44, 3.45];
  const status   = m.statusLine ?? "Charging";
  const timeLeft = m.timeLeft ?? "--";

  const capacity  = BATTERY_CAPACITY_AH;
  const remaining = Math.round((soc / 100) * capacity);

  const [lowSocWarning, setLowSocWarning] = useState(SOC_WARNING_DEFAULT);
  const [chargeEnabled, setChargeEnabled] = useState(device?.controls?.chargeEnabled ?? true);
  const [dischargeEnabled, setDischargeEnabled] = useState(device?.controls?.dischargeEnabled ?? true);

  const isCharging    = current > 0 || status === "Charging";
  const isDischarging = current < 0 || status === "Discharging";

  return (
    <SlideOverlay isOpen={isOpen}>
      <div className="h-full overflow-y-auto bg-brand-navy px-4 pb-6">
        <div className="mx-auto w-full max-w-xl space-y-4 pt-4">

          {/* ── Header ──────────────────────────────────────────────────── */}
          <div className="flex items-center justify-between">
            <button
              type="button"
              onClick={onClose}
              className="flex h-9 w-9 items-center justify-center rounded-full bg-brand-purple text-brand-light hover:bg-brand-purple/80 transition"
              aria-label="Back"
            >
              <Icon name="arrow_back" className="text-lg" />
            </button>
            <h1 className="text-lg font-black italic text-brand-light tracking-tight">
              Battery: {bmsName || "Main LiFePO4"}
            </h1>
            <div className="h-9 w-9" />
          </div>

          {/* ── Battery Identity ────────────────────────────────────────── */}
          <div className="rounded-3xl border border-brand-navy bg-brand-purple px-4 py-3 flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <span className="flex h-10 w-10 items-center justify-center rounded-full bg-brand-navy border border-brand-navy">
                <Icon name="battery_charging_full" className="text-xl text-brand-magenta" />
              </span>
              <div>
                <p className="text-sm font-black italic text-brand-light">{bmsName || "Main LiFePO4"}</p>
                <p className="text-[11px] text-brand-light/50">
                  via {bridgeName || "cBerry BMS Bridge"}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-1.5">
              <span className={`h-2 w-2 rounded-full ${isCharging ? "bg-brand-magenta" : isDischarging ? "bg-amber-400" : "bg-slate-500"}`} />
              <span className={`text-xs font-semibold ${isCharging ? "text-brand-magenta" : isDischarging ? "text-amber-400" : "text-brand-light/50"}`}>
                {status}
              </span>
            </div>
          </div>

          {/* ── SoC Arc ─────────────────────────────────────────────────── */}
          <div className="rounded-3xl border border-brand-navy bg-brand-purple px-4 py-5">
            <h2 className="text-xs font-black italic tracking-widest text-brand-light/50 uppercase mb-4">
              State of Charge
            </h2>
            <div className="flex items-center gap-6">
              {/* Arc + center value */}
              <div className="relative shrink-0">
                <SocArc soc={soc} />
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <span className="text-3xl font-black italic text-brand-light leading-none">{soc}%</span>
                  <span className="text-[10px] text-brand-light/50 mt-0.5">SoC</span>
                </div>
              </div>
              {/* Secondary metrics */}
              <div className="flex-1 space-y-3">
                <div className="flex items-center justify-between text-[11px]">
                  <span className="text-brand-light/50">Capacity</span>
                  <span className="font-semibold text-brand-light">{capacity} Ah</span>
                </div>
                <div className="flex items-center justify-between text-[11px]">
                  <span className="text-brand-light/50">Remaining</span>
                  <span className="font-semibold text-brand-light">{remaining} Ah</span>
                </div>
                <div className="flex items-center justify-between text-[11px]">
                  <span className="text-brand-light/50">SoH</span>
                  <span className="font-semibold text-brand-light">{soh}%</span>
                </div>
                <div className="flex items-center justify-between text-[11px]">
                  <span className="text-brand-light/50">{isCharging ? "Time to full" : "Time left"}</span>
                  <span className="font-semibold text-brand-magenta">{timeLeft}</span>
                </div>
              </div>
            </div>
          </div>

          {/* ── Key Metrics ─────────────────────────────────────────────── */}
          <div className="rounded-3xl border border-brand-navy bg-brand-purple px-4 py-4 space-y-3">
            <h2 className="text-xs font-black italic tracking-widest text-brand-light/50 uppercase">
              Key Metrics
            </h2>
            <div className="grid grid-cols-2 gap-2">
              <MetricTile label="Voltage" value={`${voltage.toFixed(1)} V`} icon="bolt" />
              <MetricTile
                label="Current"
                value={`${current > 0 ? "+" : ""}${current.toFixed(1)} A`}
                accent={current !== 0}
                icon="electric_meter"
              />
              <MetricTile
                label="Power"
                value={`${power > 0 ? "+" : ""}${power} W`}
                accent={power !== 0}
                icon="power"
              />
              <MetricTile label="Temperature" value={`${temp}°C`} icon="device_thermostat" />
            </div>
          </div>

          {/* ── Cell Voltage Analysis ────────────────────────────────────── */}
          <div className="rounded-3xl border border-brand-navy bg-brand-purple px-4 py-4 space-y-3">
            <div className="flex items-center justify-between">
              <h2 className="text-xs font-black italic tracking-widest text-brand-light/50 uppercase">
                Cell Voltage Analysis
              </h2>
              <span className="text-[10px] text-brand-light/40">{cells.length} cells</span>
            </div>
            <div className="space-y-2">
              {cells.map((v, i) => (
                <CellBar key={i} index={i} voltage={v} allVoltages={cells} />
              ))}
            </div>
            <div className="flex items-center justify-between text-[10px] text-brand-light/40 px-0.5">
              <span>Min: {Math.min(...cells).toFixed(3)} V</span>
              <span>Δ {(Math.max(...cells) - Math.min(...cells)).toFixed(3)} V</span>
              <span>Max: {Math.max(...cells).toFixed(3)} V</span>
            </div>
          </div>

          {/* ── Safety & Configuration ───────────────────────────────────── */}
          <div className="rounded-3xl border border-brand-navy bg-brand-purple px-4 py-4 space-y-4">
            <h2 className="text-xs font-black italic tracking-widest text-brand-light/50 uppercase">
              Safety & Configuration
            </h2>

            {/* Low SoC Warning slider */}
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="font-semibold text-brand-light">Low SoC Warning</span>
                <span className="font-black italic text-brand-magenta">{lowSocWarning}%</span>
              </div>
              <input
                type="range"
                min={SOC_WARNING_MIN}
                max={SOC_WARNING_MAX}
                step={SOC_WARNING_STEP}
                value={lowSocWarning}
                onChange={(e) => setLowSocWarning(Number(e.target.value))}
                className="w-full accent-[#BB16A3] h-1.5 rounded-full appearance-none bg-brand-navy cursor-pointer"
                style={{
                  background: `linear-gradient(to right, #BB16A3 0%, #BB16A3 ${
                    ((lowSocWarning - SOC_WARNING_MIN) / (SOC_WARNING_MAX - SOC_WARNING_MIN)) * 100
                  }%, #211747 ${((lowSocWarning - SOC_WARNING_MIN) / (SOC_WARNING_MAX - SOC_WARNING_MIN)) * 100}%, #211747 100%)`,
                }}
              />
              <div className="flex justify-between text-[10px] text-brand-light/30">
                <span>{SOC_WARNING_MIN}%</span><span>{SOC_WARNING_MAX}%</span>
              </div>
            </div>

            {/* Charge / Discharge toggles */}
            <div className="space-y-2">
              <div className="flex items-center justify-between rounded-xl border border-brand-navy bg-brand-navy px-3 py-3">
                <div className="flex items-center gap-2">
                  <Icon name="battery_charging_full" className="text-base text-brand-magenta" />
                  <span className="text-sm font-semibold text-brand-light">Charging Enabled</span>
                </div>
                <Toggle on={chargeEnabled} onChange={setChargeEnabled} suppressPropagation />
              </div>
              <div className="flex items-center justify-between rounded-xl border border-brand-navy bg-brand-navy px-3 py-3">
                <div className="flex items-center gap-2">
                  <Icon name="power" className="text-base text-brand-magenta" />
                  <span className="text-sm font-semibold text-brand-light">Discharging Enabled</span>
                </div>
                <Toggle on={dischargeEnabled} onChange={setDischargeEnabled} suppressPropagation />
              </div>
            </div>
          </div>

          {/* ── Device Settings ──────────────────────────────────────────── */}
          <div className="rounded-3xl border border-brand-navy bg-brand-purple px-4 py-4 space-y-3">
            <h2 className="text-xs font-black italic tracking-widest text-brand-light/50 uppercase">
              Device Settings
            </h2>
            <div className="space-y-1">
              <label className="text-[11px] font-semibold text-brand-light/60">Display Name</label>
              <input
                type="text"
                value={bmsName ?? ""}
                onChange={(e) => onNameChange(e.target.value)}
                className="w-full rounded-xl border border-brand-navy bg-brand-navy px-3 py-2.5 text-sm text-brand-light placeholder:text-brand-light/30 focus:outline-none focus:ring-1 focus:ring-brand-magenta"
              />
            </div>
            <div className="flex items-center justify-between rounded-xl border border-brand-navy bg-brand-navy px-3 py-3">
              <div className="flex items-center gap-2">
                <Icon name="dashboard" className="text-base text-brand-light/60" />
                <div>
                  <p className="text-sm font-semibold text-brand-light">Show on Dashboard</p>
                  <p className="text-[11px] text-brand-light/40">Surface in quick controls</p>
                </div>
              </div>
              <Toggle
                on={showOnDashboard ?? false}
                onChange={onShowOnDashboardChange}
                suppressPropagation
              />
            </div>
          </div>

          {/* ── Save ─────────────────────────────────────────────────────── */}
          <button
            type="button"
            onClick={onSave}
            className="w-full rounded-2xl bg-brand-magenta px-4 py-3.5 text-sm font-black italic text-white shadow-lg shadow-brand-magenta/30 hover:bg-brand-pink focus:ring-2 focus:ring-brand-pink focus:outline-none transition active:scale-[0.99]"
          >
            Save Changes
          </button>

        </div>
      </div>
    </SlideOverlay>
  );
}
